import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText, AlertTriangle, CreditCard, Scale } from 'lucide-react';

const EmergencyCardTerms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-[#D92B2B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              const basePath = window.location.pathname.includes('emergency-app') ? '/emergency-app' : '/';
              navigate(basePath);
            }}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Terms of Service</h1>
            <p className="text-xs text-[#FECACA]">Emergency Card</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 pb-12">
        <div className="prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Last updated: March 2, 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#D92B2B]" />
              Agreement to Terms
            </h2>
            <p className="text-slate-700">
              By using Emergency Card ("the Service"), you agree to these Terms of Service. 
              If you do not agree, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Description of Service</h2>
            <p className="text-slate-700 mb-3">
              Emergency Card is a tool that allows you to create a QR code containing your emergency medical information. 
              Key features:
            </p>
            <ul className="space-y-2 text-slate-700">
              <li>You enter your medical information into our form</li>
              <li>A QR code is generated containing your data</li>
              <li>You save this QR code to your device</li>
              <li>When scanned, the QR code displays your emergency information</li>
              <li><strong>We do not store your medical information on our servers</strong></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#D92B2B]" />
              Payment Terms
            </h2>
            <ul className="space-y-2 text-slate-700">
              <li><strong>Price:</strong> Emergency Card costs $1 USD (one-time payment).</li>
              <li><strong>Payment Processing:</strong> Payments are processed securely by Stripe.</li>
              <li><strong>Refunds:</strong> Due to the instant-delivery nature of the service, refunds are generally not available once the QR code has been generated. Contact us for exceptional circumstances.</li>
              <li><strong>No Subscription:</strong> This is a one-time purchase, not a recurring subscription.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Your Responsibilities</h2>
            <ul className="space-y-2 text-slate-700">
              <li><strong>Accurate Information:</strong> You are responsible for entering accurate medical information.</li>
              <li><strong>Keeping Your QR Code Safe:</strong> Your QR code contains your medical data. Keep it secure like any personal document.</li>
              <li><strong>Updating Information:</strong> If your medical information changes, you should create a new Emergency Card.</li>
              <li><strong>Backup:</strong> We recommend saving your QR code in multiple locations (phone, cloud backup) as we cannot recover it for you.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#D92B2B]" />
              Important Disclaimers
            </h2>
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
              <p className="text-amber-800 font-medium mb-2">Medical Disclaimer</p>
              <p className="text-amber-700 text-sm">
                Emergency Card is an informational tool only. It is NOT a substitute for professional medical advice, 
                diagnosis, or treatment. Always seek the advice of qualified health providers with questions about medical conditions.
              </p>
            </div>
            <ul className="space-y-2 text-slate-700">
              <li><strong>No Guarantee of Emergency Response:</strong> We cannot guarantee that emergency responders will scan or act upon your Emergency Card.</li>
              <li><strong>Data Accuracy:</strong> We do not verify the medical information you enter. You are solely responsible for its accuracy.</li>
              <li><strong>Service Availability:</strong> While we strive for high availability, we do not guarantee uninterrupted access to the QR viewer.</li>
              <li><strong>No Medical Advice:</strong> We do not provide medical advice or recommendations.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Scale className="h-5 w-5 text-[#D92B2B]" />
              Limitation of Liability
            </h2>
            <p className="text-slate-700 mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="space-y-2 text-slate-700">
              <li>Emergency Card is provided "AS IS" without warranties of any kind.</li>
              <li>We are not liable for any damages arising from the use or inability to use the Service.</li>
              <li>We are not liable for any medical decisions made based on the information in your Emergency Card.</li>
              <li>We are not responsible for data loss if you lose your QR code, as we do not store your data.</li>
              <li>Our total liability shall not exceed the amount you paid for the Service ($1 USD).</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Intellectual Property</h2>
            <ul className="space-y-2 text-slate-700">
              <li>The Emergency Card name, logo, and service design are our property.</li>
              <li>Your medical data encoded in the QR code remains your property.</li>
              <li>You may use your generated QR code for personal, non-commercial purposes.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Prohibited Uses</h2>
            <p className="text-slate-700 mb-3">You agree NOT to:</p>
            <ul className="space-y-2 text-slate-700">
              <li>Create Emergency Cards with false information for fraudulent purposes</li>
              <li>Attempt to reverse-engineer or exploit the Service</li>
              <li>Use the Service for any illegal purpose</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Termination</h2>
            <p className="text-slate-700">
              We reserve the right to refuse service to anyone for any reason. 
              Since we don't store your data, "termination" simply means you would not be able to create new cards. 
              Existing QR codes you've saved will continue to work.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Changes to Terms</h2>
            <p className="text-slate-700">
              We may update these Terms from time to time. Continued use of the Service after changes 
              constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Governing Law</h2>
            <p className="text-slate-700">
              These Terms shall be governed by and construed in accordance with applicable laws, 
              without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-3">Contact</h2>
            <p className="text-slate-700">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-[#D92B2B] font-medium mt-2">
              support@myemergencycard.app
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default EmergencyCardTerms;
